import React from "react";
import { Button, Text } from "@ilc-technology/luik";
import Enrich from "../../Common/services/TextEnricher";
import { useDatasources } from "../../contexts/StoryblokContext/StoryblokContext";
import { LabelKey } from "../../Common/StoryblokTypes";
import { TRUE } from "../../Common/Constants";

interface SalesUserInfoProps {
  title?: string;
  firstName: string;
  lastName: string;
  description?: string;
  phone?: string;
  email?: string;
  imageUrl?: string;
}

const SalesUserInfo: React.FC<SalesUserInfoProps> = ({
  title,
  firstName,
  lastName,
  description,
  phone,
  email,
  imageUrl,
}) => {
  const { labels, featureSettings } = useDatasources();
  const isWhatsAppButtonEnabled = featureSettings.isWhatsAppButtonEnabled === TRUE;

  return (
    <div className="bg-white p-8 lg:px-0" data-testid="sales-user-card">
      <div className="a-page-content items-left a-gap flex flex-col bg-white md:mx-auto">
        <div>
          <div data-testid="sales-user-header">
            <Text variant="heading-3-bold">{labels[LabelKey.contactUs]}</Text>
          </div>
        </div>
        <div className="a-gap flex flex-col md:flex-row">
          {imageUrl && (
            <div className="flex flex-shrink-0 items-center justify-center" data-testid="sales-user-image">
              <img
                className="h-sales-coordinator w-sales-coordinator rounded-full border-2 border-white object-cover shadow-lg ring-4 ring-white"
                src={imageUrl}
                alt="sales coordinator"
              />
            </div>
          )}
          <div className="a-gap-sm flex flex-col items-center text-center md:place-items-start md:text-left">
            <div data-testid="sales-user-header">
              <Text className="text-gray-45" variant="special-eyebrow">
                {title ?? labels[LabelKey.headerSalesUser]}
              </Text>
            </div>
            <div data-testid="sales-user-name">
              <Text variant="heading-5-bold">
                {firstName} {lastName}
              </Text>
            </div>
            <div data-testid="sales-user-description">
              <Text variant="paragraph-body">
                {description ?? Enrich(labels[LabelKey.descriptionSalesUser], { firstName: firstName })}
              </Text>
            </div>
            <div className="a-gap-sm flex w-full flex-col md:flex-row md:flex-wrap">
              {phone && (
                <div className="w-full md:w-auto" data-testid="sales-user-phone-button">
                  <Button
                    iconName="phone"
                    size="base"
                    className="h-full w-full md:w-auto"
                    intent="secondary-black"
                    onPress={() => {
                      window.open("tel:" + phone, "_self");
                    }}
                  >
                    <Text variant="label-lg">{labels[LabelKey.phoneCallButton]}</Text>
                  </Button>
                </div>
              )}
              {isWhatsAppButtonEnabled && phone && (
                <div data-testid="sales-user-whatsapp-button">
                  <Button
                    iconName="whatsapp"
                    size="base"
                    className="h-full w-full md:w-auto"
                    intent="secondary-black"
                    onPress={() => {
                      window.open("https://api.whatsapp.com/send?phone=" + encodeURIComponent(phone), "_blank");
                    }}
                  >
                    <Text variant="label-lg">
                      {window.innerWidth < 720
                        ? labels[LabelKey.sendMessageButtonMobile]
                        : labels[LabelKey.sendMessageButton]}
                    </Text>
                  </Button>
                </div>
              )}
              {email && (
                <div data-testid="sales-user-email-button">
                  <Button
                    iconName="mail"
                    size="base"
                    className="h-full w-full md:w-auto"
                    intent="secondary-black"
                    onPress={() => {
                      window.open("mailto:" + email);
                    }}
                  >
                    <Text variant="label-lg">{labels[LabelKey.sendEmailButton]}</Text>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesUserInfo;
